import React, { useEffect, useRef, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Brightness2Outlined from '@material-ui/icons/Brightness2Outlined';
import DesktopWindowsOutlined from '@material-ui/icons/DesktopWindowsOutlined';
import WbSunnyOutlined from '@material-ui/icons/WbSunnyOutlined';
import ToggleButton from '@material-ui/lab/ToggleButton';
import { useThemeMode } from 'providers/ThemeModeProvider';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { ThemeModes } from 'UI/theme/themeMode';

import { useStyles } from './ThemeModeSelector.styled';

const OPTIONS = [
  { value: ThemeModes.Light, label: strings.navBar.appearance.light, Icon: WbSunnyOutlined },
  { value: ThemeModes.Dark, label: strings.navBar.appearance.dark, Icon: Brightness2Outlined },
  {
    value: ThemeModes.System,
    label: strings.navBar.appearance.system,
    Icon: DesktopWindowsOutlined
  }
];

/**
 * Icon-only Light / Dark / System pill shown at the top of the user menu; each mode is named by its
 * `aria-label` and tooltip. Renders nothing while the dark mode feature flag is off.
 * @param {Object} props
 * @param {boolean} [props.autoFocus] set by MUI `Menu` on its first item; focuses the selected mode
 *
 * The root is a menu focus target (`tabIndex`) only while focus is outside it, so arrow keys from
 * the menu items land on the selected mode and arrow keys from the last mode move on to the items.
 */
export const ThemeModeSelector = ({ autoFocus = false }) => {
  const classes = useStyles();
  const { isEnabled, mode, setMode } = useThemeMode();
  const selectedButtonRef = useRef(null);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);

  useEffect(() => {
    if (autoFocus && isEnabled) selectedButtonRef.current?.focus();
  }, [autoFocus, isEnabled]);

  const handleFocus = event => {
    setHasFocusWithin(true);
    if (event.target === event.currentTarget) selectedButtonRef.current?.focus();
  };

  const handleBlur = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setHasFocusWithin(false);
  };

  return (
    <When condition={isEnabled}>
      <div
        className={classes.root}
        tabIndex={hasFocusWithin ? undefined : -1}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div role="group" aria-label={strings.navBar.appearance.title} className={classes.group}>
          {OPTIONS.map(({ value, label, Icon }) => (
            <Tooltip key={value} title={label} placement="bottom" arrow>
              <ToggleButton
                value={value}
                selected={value === mode}
                onChange={() => setMode(value)}
                aria-label={label}
                ref={value === mode ? selectedButtonRef : undefined}
                className={classes.button}
              >
                <Icon />
              </ToggleButton>
            </Tooltip>
          ))}
        </div>
      </div>
    </When>
  );
};

export default ThemeModeSelector;
